import { DealerType, ICard, ICardsMap, TableCommonType, TableStates } from 'types';
import { IJackpot } from './authorization';
import { IChatMessage } from './chat';

export enum Notifications {
  MESSAGE = 'MESSAGE',
  DEALER_CONNECTED = 'DEALER_CONNECTED',
  OPEN_GAME = 'OPEN_GAME',
  GAME_OPENED = 'GAME_OPENED',
  TABLE_CLOSE_VERIFY = 'VERIFY_SHIFT_MANAGER',
  TABLE_CLOSE_VERIFIED = 'VERIFY_SHIFT_MANAGER',
  VERIFY_SHIFT_MANAGER = 'VERIFY_SHIFT_MANAGER',
  CLOSE_GAME = 'CLOSE_GAME',
  GAME_CLOSED = 'GAME_CLOSED',
  CHANGE_DEALER = 'CHANGE_DEALER',
  DEALER_CHANGED = 'DEALER_CHANGED',
  START_ROUND = 'START_ROUND',
  ROUND_STARTED = 'ROUND_STARTED',
  CANCEL_ROUND = 'CANCEL_ROUND',
  ROUND_CANCELED = 'ROUND_CANCELED',
  BETTING_TIME_STARTED = 'BETTING_TIME_STARTED',
  BETTING_TIME_FINISHED = 'BETTING_TIME_FINISHED',
  START_DRAW = 'START_DRAW',
  DRAW_STARTED = 'DRAW_STARTED',
  DRAW_FINISHED = 'DRAW_FINISHED',
  CONFIRM_RESULT = 'CONFIRM_RESULT',
  RESULT_CONFIRMED = 'RESULT_CONFIRMED',
  ROUND_FINISHED = 'ROUND_FINISHED',
  CHANGE_RESULT = 'CHANGE_RESULT',
  RESULT_CHANGED = 'RESULT_CHANGED',
  GAME_MACHINE_CONNECTION = 'GAME_MACHINE_CONNECTION',
  NOTIFICATION = 'NOTIFICATION',
  PONG = 'PONG',
  ERROR = 'ERROR',

  JACKPOT_STAGE_STARTED = 'JACKPOT_STAGE_STARTED',
  JACKPOT_FINISHED = 'JACKPOT_FINISHED',
  JACKPOT_STATE_UPDATED = 'JACKPOT_STATE_UPDATED',

  ADD_NEW_CARD = 'ADD_NEW_CARD',
  NEW_CARD_ADDED = 'NEW_CARD_ADDED',
  WAITING_NEXT_CARD = 'WAITING_NEXT_CARD',
  SHUFFLE_REQUIRED = 'SHUFFLE_REQUIRED',
  SHUFFLE = 'SHUFFLE',
  CANCEL_LAST_CARD = 'CANCEL_LAST_CARD',
  CANCEL_ALL_CARDS = 'CANCEL_ALL_CARDS',
  LAST_CARD_CANCELED = 'LAST_CARD_CANCELED',
  ALL_CARDS_CANCELED = 'ALL_CARDS_CANCELED',
  CHANGE_CARDS = 'CHANGE_CARDS',
  CARDS_CHANGED = 'CARDS_CHANGED',

  CHAT_TOKEN_PROVIDED = 'CHAT_TOKEN_PROVIDED',
}

export enum NotificationsChat {
  CHAT_DEALER_CONNECTED = 'CHAT_DEALER_CONNECTED',

  NEW_CHAT_MESSAGE = 'NEW_CHAT_MESSAGE',
}

export enum ClientSideNotifications {
  SOCKET_CONNECTED = 'connect',
  SOCKET_DISCONNECTED = 'disconnect',
}

export enum ErrorNotifications {
  CONNECT_ERROR = 'connect_error',
  ERROR = 'error',
}

export interface INotificationData {
  code: string;
  message: string;
  payload: object;
}

export interface INotificationResponse<T> {
  data: T;
  type: Notifications;
}

export interface INotificationChatResponse<T> {
  data: T;
  type: NotificationsChat;
}

export interface IDataResponse {
  status: number;
  message: string;
}

interface IDealerJoinedResponseData {
  gameId: string;
  dealer: DealerType;
  last_balls?: string[];
  lastWinners?: number[];
  status: TableStates;
  jackpot?: IJackpot;
  round?: {
    roundId: string;
    drawTime?: number;
    winner?: string;
    drawStartedAt?: string;
    startedAt?: string;
    finishBettingTime?: string;
    finishResultDisplay?: string;
    finishDrawnDisplay?: string;

    cards?: ICardsMap;
    waitingNextCardPosition?: number;
  };
  configuration: TableCommonType;
  waitingShuffle?: boolean;
}

interface ITableOpenedResponseData {
  configuration: TableCommonType;
}

interface ITableCloseVerifyResponseData {}

interface ITableClosedResponseData {}

interface IDealerChangedResponseData {
  dealer: DealerType;
}

interface IRoundStartedResponseData {
  round: {
    roundId: string;
  };
}

interface ISocketPongData {
  token: string;
}

interface IRoundCanceledResponseData {
  fromAdmin: boolean;
}

interface IBettingTimeStartedResponseData {
  bettingTime: number;
  finishBettingTime: string;
}

interface IBettingTimeFinishedResponseData {}

interface IDrawStartedResponseData {
  drawStartedAt: string;
}

interface IDrawFinishedResponseData {
  winner: string;
  drawTime: number;
  finishDrawnDisplay: string;
}

interface IResultConfirmedResponseData {
  winner: string;
  finishResultDisplay: string;
}
interface IResultChangedResponseData {}

interface IRoundFinishedResponseData {}

interface IGameMachineConnectionResponseResponseData {
  status: 'CONNECTED' | 'DISCONNECTED';
}

interface IErrorNotificationResponseData {
  event: string;
  reason: string;
  status: number;
}

interface IChatTokenProvidedNotificationResponse {
  tableId: string;
  token: string;
}

interface IChatDealerJoinedResponseData {
  tableId: string;
  chatHistory: IChatMessage[];
}

interface INewChatMessageNotificationResponse {
  playerName: string;
  message: string;
  fromSupport: boolean;
}

interface IAddedCardResponseData {
  card: ICard;
  position: number;
}

interface IWaitingNextCardResponseData {
  position: number;
}

interface ITableNotificationResponseData {
  notifyAbout?: string;
}

interface ILastCardCanceledResponseData {
  cards: ICardsMap;
}

interface IAllCardsCanceledResponseData {}

interface ICardsChangedResponseData {
  cards: ICardsMap;
}

export type DealerJoinedResponse = INotificationResponse<IDealerJoinedResponseData>;
export type TableOpenedResponse = INotificationResponse<ITableOpenedResponseData>;
export type TableCloseVerifyResponse = INotificationResponse<ITableCloseVerifyResponseData>;
export type TableClosedResponse = INotificationResponse<ITableClosedResponseData>;
export type DealerChangedResponse = INotificationResponse<IDealerChangedResponseData>;
export type RoundStartedResponse = INotificationResponse<IRoundStartedResponseData>;
export type RoundCanceledResponse = INotificationResponse<IRoundCanceledResponseData>;
export type BettingTimeStartedResponse = INotificationResponse<IBettingTimeStartedResponseData>;
export type BettingTimeFinishedResponse = INotificationResponse<IBettingTimeFinishedResponseData>;
export type DrawStartedResponse = INotificationResponse<IDrawStartedResponseData>;
export type DrawFinishedResponse = INotificationResponse<IDrawFinishedResponseData>;
export type ResultConfirmedResponse = INotificationResponse<IResultConfirmedResponseData>;
export type ResultChangedResponse = INotificationResponse<IResultChangedResponseData>;
export type RoundFinishedResponse = INotificationResponse<IRoundFinishedResponseData>;
export type GameMachineConnectionResponse =
  INotificationResponse<IGameMachineConnectionResponseResponseData>;
export type ChatTokenProvidedResponse =
  INotificationResponse<IChatTokenProvidedNotificationResponse>;
export type ChatDealerJoinedResponse = INotificationChatResponse<IChatDealerJoinedResponseData>;
export type NewChatMessageNotificationResponse =
  INotificationChatResponse<INewChatMessageNotificationResponse>;
export type SocketPongResponse = INotificationResponse<ISocketPongData>;
export type ErrorNotificationResponse = INotificationResponse<IErrorNotificationResponseData>;
export type TableNotificationResponse = INotificationResponse<ITableNotificationResponseData>;
export type AddedCardResponse = INotificationResponse<IAddedCardResponseData>;
export type WaitingNextCardResponse = INotificationResponse<IWaitingNextCardResponseData>;
export type LastCardCanceledResponse = INotificationResponse<ILastCardCanceledResponseData>;
export type AllCardsCanceledResponse = INotificationResponse<IAllCardsCanceledResponseData>;
export type CardsChangedResponse = INotificationResponse<ICardsChangedResponseData>;

export type PossibleResponse =
  | DealerJoinedResponse
  | TableOpenedResponse
  | TableCloseVerifyResponse
  | TableClosedResponse
  | DealerChangedResponse
  | RoundStartedResponse
  | RoundCanceledResponse
  | BettingTimeStartedResponse
  | BettingTimeFinishedResponse
  | DrawStartedResponse
  | DrawFinishedResponse
  | ResultConfirmedResponse
  | RoundFinishedResponse
  | ChatTokenProvidedResponse
  | ChatDealerJoinedResponse
  | NewChatMessageNotificationResponse
  | AddedCardResponse
  | WaitingNextCardResponse
  | LastCardCanceledResponse
  | AllCardsCanceledResponse
  | CardsChangedResponse
  | TableNotificationResponse
  | ErrorNotificationResponse;
